import { useEffect } from 'react';

import {
  ApplicationStatusUI,
  useGetApplicationByIdQuery,
} from '~domains/commerce/transactions/entities/application';
import { useGenerateDocumentMutation } from '~domains/commerce/transactions/entities/document';

import { useHasPermissions } from '~platform/auth';

import { Loader } from '~shared/ui/loader';
import { MoneyPh } from '~shared/ui/money-ph';

import { Button } from '@breezeventures/ui-kit/button';
import { Typography } from '@breezeventures/ui-kit/typography';
import { format } from 'date-fns';

import type { ApplicationsTableData } from '../../model/applications-table-data';

const EMPTY_VALUE = '—';

type DetailRowProps = {
  label: string;
  children: React.ReactNode;
};

const DetailRow = ({ label, children }: DetailRowProps) => (
  <li className="grid grid-cols-2 items-start gap-4 px-4 py-4">
    <Typography.Text size="m" color="secondary" tag="span">
      {label}
    </Typography.Text>
    <Typography.Text size="m" tag="span" className="min-w-0 text-left">
      {children}
    </Typography.Text>
  </li>
);

const detailTableClassName = `
  m-0 list-none divide-y divide-separator overflow-hidden rounded-xl
  border border-separator p-0
`;

export const ApplicationDetails = ({
  application,
}: {
  application: ApplicationsTableData;
}) => {
  const { data: fullApplication, isLoading } = useGetApplicationByIdQuery(
    application.applicationId
  );
  const { checkPermissions } = useHasPermissions();
  const canGenerateLoanNotification = checkPermissions([
    'LOAN_NOTIFICATION_GENERATE',
  ]);

  const {
    mutate: generateDocument,
    isPending: isGenerationInProgress,
    data: documentData,
  } = useGenerateDocumentMutation();

  useEffect(() => {
    if (documentData) {
      const file = new Blob([documentData], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    }
  }, [documentData]);

  const dateObj = new Date(application.applicationTimestampUTC);
  const orderItems = fullApplication?.orderItems ?? [];
  const hasItems = orderItems.length > 0;

  const handleGenerateDocument = () => {
    generateDocument(application.applicationId);
  };

  return (
    <div className="flex flex-col gap-4 pb-2">
      <ul className={detailTableClassName}>
        <DetailRow label="Agreement ID">
          {application.agreementId || EMPTY_VALUE}
        </DetailRow>

        <DetailRow label="Status">
          <ApplicationStatusUI status={application.applicationStatus} />
        </DetailRow>

        <DetailRow label="Date & time">
          <span className="flex flex-col items-start gap-0.5">
            <span>{format(dateObj, 'dd.MM.yyyy')}</span>
            <Typography.Text size="xs" color="secondary" tag="span">
              {format(dateObj, 'hh:mm a')}
            </Typography.Text>
          </span>
        </DetailRow>

        <DetailRow label="Store">
          {application.pos.fullAddress || EMPTY_VALUE}
        </DetailRow>

        <DetailRow label="Sale amount">
          <MoneyPh value={application.saleAmount} />
        </DetailRow>

        <DetailRow label="Down payment">
          <MoneyPh value={application.downPaymentAmount} />
        </DetailRow>

        <DetailRow label="Financed amount">
          <MoneyPh value={application.financedAmount} />
        </DetailRow>
      </ul>

      {(isLoading || hasItems) && (
        <div className="flex flex-col gap-2">
          <Typography.Text
            size="m"
            weight="medium"
            color="primary"
            tag="span"
            className="px-1"
          >
            Purchased items
          </Typography.Text>
          <ul className={detailTableClassName}>
            {isLoading ? (
              <li className="flex min-h-[64px] items-center justify-center py-4">
                <Loader overlay={false} size="s" />
              </li>
            ) : (
              orderItems.map((item) => (
                <DetailRow
                  key={item.name}
                  label={`${item.name}${item.isUsed ? ' (Used)' : ''}`}
                >
                  x&nbsp;{item.quantity}
                </DetailRow>
              ))
            )}
          </ul>
        </div>
      )}

      {canGenerateLoanNotification && (
        <Button
          size="l"
          width="full"
          loading={isGenerationInProgress}
          onClick={handleGenerateDocument}
        >
          See loan notification
        </Button>
      )}
    </div>
  );
};
